import { useAtomValue } from 'jotai';
import { useLocalStorage } from 'react-use';

import { Subhead, Tag } from '@growtherapy/sprout-ui';

import {
  type CSSRSPromptResult,
  CSSRSType,
  ClientMeasureOptOutBannerConnected,
  ClientMeasureOptOutReminderBannerConnected,
} from '@grow-therapy-team/seedling-components';

import {
  CSSRSFormListItemWrapper as CSSRSFormListItem,
  IntakeFormListItemWrapper as IntakeFormListItem,
  MeasuresFormListItemWrapper as MeasuresFormListItem,
  SafetyPlanFormListItemWrapper as SafetyPlanFormListItem,
} from '../measures/FormListItemsWrapper';
import { selectedClientUserShortIdAtom } from '../state';
import {
  CLIENT_OPT_OUT_REMINDER_BANNER_DISMISSED,
  CLIENT_OPT_OUT_REMINDER_BANNER_DISMISSED_VALUE,
  MEASURES_AND_FORMS_TITLE,
} from './constants';
import { CombinedFormDataEntriesByFormTitle } from './types';

export type ClientInformationFormsAndQuestionnairesProps = {
  completedForms?: CombinedFormDataEntriesByFormTitle;
  hasNewForms?: boolean;
  isInSessionWithSelectedClient?: boolean;
  shouldShowAnytimeCSSRS?: boolean;
  cssrsPromptLoading: boolean;
  cssrsPromptResult: CSSRSPromptResult;
};

export function ClientInformationFormsAndQuestionnaires({
  hasNewForms,
  isInSessionWithSelectedClient,
  shouldShowAnytimeCSSRS,
  cssrsPromptLoading,
  cssrsPromptResult,
}: ClientInformationFormsAndQuestionnairesProps) {
  const clientUserShortId = useAtomValue(selectedClientUserShortIdAtom);

  const [clientOptOutReminderBannerDismissed, setClientOptOutReminderBannerDismissed] = useLocalStorage(
    CLIENT_OPT_OUT_REMINDER_BANNER_DISMISSED,
    'false',
  );
  const shouldShowCSSRS =
    isInSessionWithSelectedClient &&
    shouldShowAnytimeCSSRS &&
    !cssrsPromptLoading &&
    cssrsPromptResult.cssrsType !== CSSRSType.TooYoung;

  return (
    <div className='flex flex-col' data-testid='client-info.measures-and-forms'>
      <Subhead.Default as='h3' className='mb-8 text-large font-medium rebrand:text-text-inverse rebrand:font-semibold'>
        {MEASURES_AND_FORMS_TITLE}
        {hasNewForms && (
          <Tag aria-label='Client has new forms' use='lilac' className='ml-2'>
            New
          </Tag>
        )}
      </Subhead.Default>
      <>
        {clientUserShortId && (
          <ClientMeasureOptOutReminderBannerConnected
            renderContext='provider'
            clientUserShortId={clientUserShortId}
            handleClose={() => setClientOptOutReminderBannerDismissed(CLIENT_OPT_OUT_REMINDER_BANNER_DISMISSED_VALUE)}
            isClosed={clientOptOutReminderBannerDismissed === CLIENT_OPT_OUT_REMINDER_BANNER_DISMISSED_VALUE}
            baseBanner={
              <ClientMeasureOptOutBannerConnected clientUserShortId={clientUserShortId} renderContext='telehealth' />
            }
          />
        )}
        {shouldShowCSSRS && <CSSRSFormListItem />}
        <MeasuresFormListItem />
        <IntakeFormListItem />
        <SafetyPlanFormListItem />
      </>
    </div>
  );
}
